import { CloseOutlined } from '@ant-design/icons';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import classNames from 'classnames';
import React, { useState } from 'react';
import { OtherContactDetails } from '../../../liveChat/components/Contact/components/OtherContactDetails';
import { ContactDeleteModal } from '../ContactDeleteModal/ContactDeleteModal';
import ContactsDetailsHeader from './ContactDetailsHeader/ContactDetailsHeader';
import { ChatVariablesTab } from './ContactDetailsTabs/ChatVariablesTab/ChatVariablesTab';
import { CustomerJourney } from './ContactDetailsTabs/CustomerJourney/CustomerJourney';
import { LeadScoring } from './ContactDetailsTabs/LeadScoring/LeadScoring';
import './ContactsDetails.scss';

type Props = {
  selectedContact: SelectedContact;
  closeDetails: () => void;
  onDeleteSelectedContact: (
    id: number,
    deleteRemoteMessages: boolean,
  ) => Promise<void>;
  onContactSelect?: (id: number) => void;
  refreshSelectedContact: (busyComponent?: string) => void;
  withSessions?: boolean;
  busy?: string[];
};

export const BUSINESS_EMAIL = 'VOY_BUSINESS_EMAIL';
export const BUSINESS_PHONE = 'VOY_COMPANY_PHONE';

export const ContactsDetails = (props: Props) => {
  const {
    selectedContact,
    closeDetails,
    onDeleteSelectedContact,
    refreshSelectedContact,
    onContactSelect = () => {},
    withSessions,
    busy,
  } = props;

  const displayVariables = selectedContact.variables?.filter(
    v => v.variable.name !== 'fb messenger user id',
  );

  const isBusinessContact = !!displayVariables.find(
    ({ variable }) => variable.name.toLocaleLowerCase() === 'business_contact',
  );

  const hasLeadScoring = !!Object.values(
    selectedContact.leadScoring || {},
  ).filter(Boolean).length;

  const hasCustomerJourney = !!selectedContact.customerJourney?.length;

  const onContactClick = (contact: SelectedContact) =>
    onContactSelect(contact.id);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <div className="contact-details">
      {closeDetails && (
        <CloseOutlined
          className="contact-details__close-button"
          onClick={closeDetails}
        />
      )}
      <ContactsDetailsHeader
        deleteSelectedContact={() => setShowDeleteModal(true)}
        selectedContact={selectedContact}
        onChangeContactInfo={refreshSelectedContact}
        isBusinessContact={isBusinessContact}
      />
      <div className="contact-details__blocks">
        <div className="contact-details__blocks-expanded">
          <LeadScoring
            leadScoring={selectedContact.leadScoring}
            className={classNames({
              'contact-details__customer-journey-full-width':
                !hasCustomerJourney,
            })}
          />
          <CustomerJourney
            customerJourney={selectedContact.customerJourney}
            formFills={selectedContact.formFills}
            className={classNames({
              'contact-details__customer-journey-full-width': !hasLeadScoring,
            })}
          />
        </div>

        <ChatVariablesTab
          contactId={selectedContact.id}
          selectedContact={selectedContact}
          variables={displayVariables}
          onAttributeUpdated={refreshSelectedContact}
          remoteUserId={selectedContact.remoteId}
          isBusinessContact={isBusinessContact}
        />
        <OtherContactDetails
          selectedContact={selectedContact}
          onChange={refreshSelectedContact}
          sessionsCallback={closeDetails}
          onContactClick={onContactClick}
          withSessions={withSessions}
          busy={busy}
        />
      </div>
      <ContactDeleteModal
        showEraseRemoteMessagesOption={selectedContact.remoteId.startsWith(
          'em_',
        )}
        show={showDeleteModal}
        emailAddress={selectedContact.email}
        onClose={() => setShowDeleteModal(false)}
        onDelete={eraseRemoteMessages =>
          onDeleteSelectedContact(selectedContact.id, eraseRemoteMessages)
        }
      />
    </div>
  );
};
